import { DeliveryContent, DeliveryContentType, DeliveryQuickCheckOutReason } from 'api/models/Deliveries';
import { SetTerminology } from 'api/models/Users';
import { DeviceRepsColumnId } from 'pages/DeviceReps/models';
import { ImplantListColumnId } from 'pages/Implants/models';
import { OrdersColumnId } from 'pages/Orders/models';
import { SurgeonsColumnId } from 'pages/Surgeons/models';

const en = {
  HomePage: {
    title: 'Welcome to SAVI-Connect - {{Set}} and Device Inspector',
    content:
      'SAVI-Connect is a web based application enabling users to easily manage, interact with, and QA medical {{Set}} and device data including state, completeness, and delivery logistics.',
  },
  ReportsPage: {
    title: 'Reports',
    noReportsAvailable: 'No reports are currently available to you',
    pleaseWaitLoadingData: 'Please wait, loading data...',
    table: {
      title: 'Title',
      actions: 'View',
    },
    reportTitles: {
      'Supply Chain Performance': 'Supply Chain Performance',
      'Supplier Performance': 'Supplier Performance',
      'Orders By Day': 'Orders By Day',
      'Active Technicians': 'Active Technicians',
      'Active Surgeons': 'Active Surgeons',
      'Detailed Instrument Sets/Trays Report': 'Detailed Instrument {{Sets}}/Trays Report',
    },
    viewReport: 'View Report',
  },
  ActionItemsPage: {
    title: 'Action Items',
    dateToday: 'Date Today',
    featureComingSoon: 'Feature Coming Soon!',
    ActionItemsMenu: {
      criticalItems: {
        label: 'Critical Items',
        allClear: 'All clear! no delays or critical items in this list!',
      },
      bookingsToBeConfirmed: {
        label: 'Bookings to be Confirmed',
        allClear: 'All clear! All Bookings have been confirmed!',
      },
      deliveriesToBeReceived: {
        label: 'Deliveries to be Received',
        allClear: 'All clear! All Deliveries have been received!',
      },
      deliveriesToBeCheckedIn: {
        label: 'Deliveries to be Checked In',
        allClear: 'All clear! All Deliveries have been checked in!',
      },
      deliveriesToBeCheckedOut: {
        label: 'Deliveries to be Checked Out',
        allClear: 'All clear! All Deliveries have been checked out!',
      },
    },
  },
  DeliveryListPage: {
    title: 'Delivery List',
    pastDeliveriesTitle: 'Past Deliveries',
    table: {
      express: 'Express',
      turnaround: 'Turnaround',
      supplier: 'Supplier',
      orderNumber: 'Order No.',
      deliveryNumber: 'Delivery No.',
      inspectionStatus: 'Status',
      shipmentId: 'Shipment ID',
      eta: 'Shipment ETA',
      priorityRequests: 'Priority Requests',
      contents: 'Delivery Contents',
      surgeryReference: 'Surgery ID',
      clinicalArea: 'Clinical Area',
      surgeon: 'Surgeon',
      patientInitials: 'Patient Initials',
      surgeryDate: 'Surgery Date',
      dueDate: {
        label: 'Return Date',
        tooltip: {
          title: 'Editable Field',
          body: 'Click an eligible Return Date field to update it',
        },
      },
      returnDate: 'Return Date',
      checkInCheckOut: 'Check In/Out',
      viewDelivery: 'View Delivery',
      noResults: 'No results',
      status: 'Status',
      turnaroundStage: 'Turnaround Status', // FYI this is intentional - The FE designs call it Status while the BE calls it Stage
      urgent: 'Urgent',
    },
    metaData: {
      total: 'Total Inspections',
      notStarted: 'Inspections not started',
      complete: 'Completed Inspections',
    },
    filterModal: {
      heading: 'Filter',
      status: 'Filter status',
      date: 'Shipping Date Range',
      dateFrom: 'From',
      dateTo: 'To',
      clearFiltersButton: 'Clear Filters',
      applyFiltersButton: 'Apply Filters',
      closeButton: 'Close',
      today: 'Today',
      tomorrow: 'Tomorrow',
      statusOptions: [
        { value: 'UNCONFIRMED', label: 'Not started' },
        { value: 'CONFIRMED', label: 'Complete' },
      ] as {
        value: string;
        label: string;
      }[],
    },
  },
  SurgeryListPage: {
    title: 'Surgery List',
    pastSurgeries: 'Past Surgeries',
    table: {
      orderStage: 'Status',
      surgeryReference: 'Surgery ID',
      dmRep: 'Device Manufacturer Rep',
      supplier: 'Supplier',
      surgeon: 'Surgeon',
      traysCount: 'No. of Trays',
      implantsCount: 'No. of Implants',
      surgeryDate: 'Surgery Date',
      surgeryDescription: 'Surgery Description',
      surgeryComments: 'Internal Notes',
      patientInitials: 'Patient Initials',
      deliveriesReceived: 'Deliveries Received',
      deliveryContent: 'Contents',
      createdBy: 'Created By',
      noResults: 'No results',
    },
    metaData: {
      total: 'Total Inspections',
      notStarted: 'Inspections not started',
      complete: 'Completed Inspections',
    },
    filterModal: {
      heading: 'Filter',
      status: 'Filter status',
      date: 'Shipping Date Range',
      dateFrom: 'From',
      dateTo: 'To',
      clearFiltersButton: 'Clear Filters',
      applyFiltersButton: 'Apply Filters',
      closeButton: 'Close',
      today: 'Today',
      tomorrow: 'Tomorrow',
      statusOptions: [
        { value: 'UNCONFIRMED', label: 'Not started' },
        { value: 'CONFIRMED', label: 'Complete' },
      ] as {
        value: string;
        label: string;
      }[],
    },
    receiveDelivery: 'Receive Delivery',
    viewSurgery: 'View Surgery',
    createNewSurgery: 'Create New Surgery',
  },
  CheckInPage: {
    title: 'Delivery Information',
    addSets: 'Add Instrument {{Sets}}',
    viewSurgery: 'View Surgery Information',
    sets: 'Instrument {{Sets}}',
    implants: 'Sterile Implants',
    serialBarcode: 'Serial/Barcode',
    scanBarcode: 'Scan Barcode',
    unknownBarcode: 'Unknown Barcode',
    setNameExists: "Instrument {{Set}} Name '{setName}' exists",
    setAdded: 'Instrument {{Set}} added',
    failedToAddSet: 'Failed to add Instrument {{Set}}',
    setName: 'Instrument {{Set}} Name',
    checkInSets: 'Check In',
    completeCheckIn: 'Save',
    deliveryCheckInCompleted: 'Delivery Check In Completed',
    printAllDeliveryInformation: 'Print All Delivery Information',
    missingRequiredFields: 'Surgery ID, Supplier, Surgeon, Surgery Date and Patient Initials are required',
  },
  DeleteSetDialog: {
    deleteSet: 'Delete {{Set}}',
    deletedSet: 'Deleted {{Set}}',
  },
  ViewDispatchPage: {
    sets: 'Instrument {{Sets}}',
    setsInDispatchedOrder: 'Instrument {{Sets}} in Dispatched Order',
    receive: 'Receive Dispatched',
    dispatchedSets: 'Instrument {{Sets}} Dispatched',
    deliveryReceiveCompleted: 'Delivery received',
  },
  ImplantsPage: {
    proceed: 'Proceed',
    rejectScannedBarcode: 'Reject Scanned Barcode',
    acceptScannedBarcode: 'Accept Scanned Barcode',
    addBarcodeNumber: 'Add Barcode Number',
    titleCheckIn: 'Check In Sterile Implants',
    titleCheckOut: 'Checked Out Sterile Implants',
    titleDispatched: 'Dispatched Sterile Implants',
    checkInImplantsToContinue: 'Check in sterile implants to continue',
    checkOutImplantsToContinue: 'Check out sterile implants to continue',
    totalImplantsSent: 'Total Sterile Implants Sent',
    totalImplantsCheckedIn: 'Total Sterile Implants Checked In',
    totalImplantsDispatched: 'Total Sterile Implants Dispatched',
    totalImplantsCheckedOut: 'Total Sterile Implants Checked Out',
    implantsSentBySupplier: 'Sterile Implants Sent by Supplier',
    checkedOutWithDelivery: 'Checked out with delivery',
    checkedIntoADifferentDelivery: 'This implant was checked into a different delivery to the current one.',
    crossDelivery: 'Cross Delivery',
    crossSurgery: 'Cross Surgery',
    notCheckedIn: 'This implant was not checked in',
    notCheckedInPlural: 'These sterile implants were not checked in',
    implantDescription: 'Implant Description',
    checkedInNotDispatched: 'Implant not dispatched in this Order',
    quantity: 'Quantity',
    noImplantListProvided: 'No implant list provided by Supplier, scan in manually',
    scanImplant: 'Scan a implant Data Matrix Code of the GS-1 Barcode to begin',
    scanBarcodeToBegin: 'Scan a Sterile Implant barcode to begin',
    scanImplantKitCodeToBegin: 'Scan a Sterile Implant barcode or a Implant Kit barcode to begin',
    checkedInImplants: 'Checked In Sterile Implants',
    implantList: 'View Sterile Implant List',
    captureNonScannableItems: 'Capture Photo of Sterile Implants with No Barcode',
    scanBarcode: 'Scan Barcode',
    deleteSuccess: 'The implant was successfully deleted',
    deleteKitSuccess: 'The implant kit was successfully deleted',
    successfulScan: 'Successful Scan',
    processingScan: 'Processing scan...',
    gs1NotFound: 'GS1 not found',
    lotNumber: 'Lot Number',
    expiry: 'Expiry',
    barcodeNumber: 'Barcode Number',
    unsuccessfulScan: 'Unsuccessful Scan, please review',
    unableToAddImplant: 'Unable to add implant to delivery, this barcode was not scannable',
    noGs1AustraliaRecord: 'No GS1 Australia Record',
    gtin: 'GTIN',
    gs1AustraliaRecordFound: 'GS1 Australia Record Found',
    description: 'Description',
    manufacturerInternalReference: 'Manufacturer Internal Reference',
    none: 'None',
    noBarcodeReadable: 'No Barcode Readable',
    implantKitRecordFound: 'Implant Kit Record Found',
    setName: '{{Set}} Name',
    setCode: '{{Set}} Code',
    uniqueSerialNumber: 'Unique Serial Number (Equipment Number)',
    numberOfImplants: 'Number of Implants',
    individualSterileImplants: 'Individual Sterile Implants',
    toggleOnToAcceptNonGS1BarcodesAutomatically: 'Toggle on to accept on GS1 Barcodes automatically',
    toggleOnToAcceptImplantKitCodesAutomatically:
      'Toggle on to accept Implant Kit Codes / Non GS1 Barcodes Automatically',

    columns: {
      [ImplantListColumnId.ReferenceOrBarcodeNumber]: 'Reference / Barcode Number',
      [ImplantListColumnId.Description]: 'Description',
      [ImplantListColumnId.BatchLot]: 'Batch Lot',
      [ImplantListColumnId.Expiry]: 'Expiry',
      [ImplantListColumnId.Dispatched]: 'Dispatched',
      [ImplantListColumnId.CheckedIn]: 'Checked In',
      [ImplantListColumnId.CheckedOut]: 'Checked Out',
      [ImplantListColumnId.Quantity]: 'Quantity',
      [ImplantListColumnId.ImplantKit]: 'Implant Kit',
    },
  },
  SkipImplantProcessingButton: {
    skipImplantProcessing: 'Skip Implant Processing',
    skipCheckInImplants: 'Skip Implant Check In',
    skipCheckOutImplants: 'Skip Implant Check Out',
  },
  ImplantListPage: {
    title: 'Sterile Implant List',
    surgeryImplantList: 'Surgery Sterile Implant List',
    printImplantList: 'Print Sterile Implant List',
    reasonForSkippingCheckIn: 'Reason for Skipping Check In: ',
    reasonForSkippingCheckOut: 'Reason for Skipping Check Out: ',
    totalImplantsSkipped: 'Total Sterile Implants Skipped: ',
    back: 'Back',
    checkIn: 'Check In Sterile Implants',
    checkOut: 'Check Out Sterile Implants',
  },
  ContinueImageCaptureDialog: {
    title: 'Capture another image or Complete Implant Check In',
    completeImplantCheckIn: 'Complete Implant Check In',
    captureAnotherImage: 'Capture another image',
  },
  DeleteTrayDialog: {
    title: 'Delete Tray',
    heading: 'Are you sure you want to delete tray {trayName}?',
    confirm: 'Yes',
    cancel: 'No',
    trayDeleted: 'Tray deleted',
    trayAndSetDeleted: 'Tray and {{Set}} deleted',
  },
  CheckInTrayPage: {
    title: 'Check in {{Set}}',
    addMissingDevice: 'Confirm missing devices',
    cancel: 'Cancel',
    loadPictureList: 'Load Picture List',
    manufacturerProvider: 'Device Manufacturer Provided BOM / Picture List:',
    listMissingDevices: 'List Missing Devices',
    preImageCaptureInspection: 'Pre Image Capture Inspection:',
    qualityCheck: 'Quality Check',
    result: 'Result',
    allDevicesClean: 'All Devices Clean of Debris?',
    pass: 'Pass',
    fail: 'Fail',
    yes: 'Yes',
    no: 'No',
    trayUsed: 'Tray Used',
    allDevicesFunctional: 'All Devices Functional?',
    allDevicesInTray: 'All Devices in Tray',
    missingDevices: 'No, List Missing Devices',
    invalidSetId: 'Invalid {{Set}} id',
    invalidTrayId: 'Invalid tray id',
    listMissingDevicesButtonText: 'No, list missing devices',
    captureTrayImage: 'Capture Tray Image',
    proceed: 'Proceed',
  },
  TraySummary: {
    update: 'update',
    editInspection: 'Edit Inspection',
    captureImage: 'Capture Image',
    loadImage: 'Load Image',
    allClean: 'All devices are clean',
    notClean: 'Device/s did not pass debris inspection',
    trayNotUsed: 'Tray not used',
    trayUsed: 'Tray Used',
    allPartsPresent: 'All parts present',
    allPartsNotPresentSingular: 'This device was missing: ',
    allPartsNotPresentPlural: 'These devices were missing: ',
    comments: 'Comments: ',
    noComments: 'No Comments',
    allFunctional: 'All functional',
    allNotFunctional: 'Device/s did not pass function test',
    tbc: 'TBC',
    editTrayName: 'Edit Tray Name',
    trayNameUpdated: 'Tray name updated',
    noImage: 'No Image Provided',
    editSadlTrayNameTooltip: "You can't edit this tray name because this {{Set}} is managed by the supplier",
    expand: 'Expand',
    collapse: 'Collapse',
  },
  CapturedImagePreview: {
    rotateImage: 'Rotate Image',
  },
  CheckInTrayDetailsPage: {
    title: ({ setName, trayName, referenceNumber }: { setName: string; trayName: string; referenceNumber: string }) =>
      `Check In {{Set}}: ${setName} - ${trayName}${referenceNumber ? `: ${referenceNumber}` : ''}`,
    retakePhoto: 'Retake Photo',
    continue: 'Continue',
    heading: 'Check In Inspection Details',
    qualityCheck: 'Quality Check',
    pass: 'Pass',
    fail: 'Fail',
    allDevicesCleanOfDebris: 'All Devices Clean of Debris?',
    allDevicesFunctional: 'All Devices Functional?',
    allDevicesInTray: 'All Devices in Tray?',
    result: 'Result',
    commentsLabel: 'Check In Comments',
    trayNumber: 'Tray Number',
    assetNumber: 'Asset Id (Barcode)',
    commentsPlaceholder: 'Please list any missing instruments here',
    trayCheckInSuccess: 'Tray check in success',
    setCheckInSuccess: '{{Set}} check in success',
    viewStandardPictureList: 'View Standard Picture List (BOM)*',
    viewLastDmInspection: 'View Last Device Manufacturer Inspection',
    viewInstructions: 'View Instructions for Use',
    noInspection: 'Not yet inspected',
    inspectionSkipped: 'Inspection skipped',
    duplicateTrayNumberError: 'Tray number already exists within this surgery',
  },
  CheckOutTrayDetailsPage: {
    commentsLabel: 'Check Out Comments',
    commentsPlaceholder: 'Please list any missing instruments here',
    noInspection: 'Not yet inspected',
    inspectionSkipped: 'Inspection skipped',
    trayNumber: 'Tray Number',
    setCheckOutSuccess: '{{Set}} check out success',
    saveError: 'Failed to save',
    imageUploadFailed: 'Failed to upload a tray image. You may need to re-attempt check out.',
    notifyCheckedOutEmailFailed: 'Failed to send Check Out notification email',
  },
  SetManualsModal: {
    title: 'Instructions for Use',
    setManualLabel: '{{Set}} {setLabel} Manual',
    noManualsAvailable: 'No manuals available for this {{Set}}',
    view: 'View',
    tableHeaders: {
      type: 'Type',
      code: 'Code',
      name: 'Name',
      manual: 'Manual',
    },
  },
  CheckOutPage: {
    viewSurgery: 'View Surgery Information',
    sets: 'Instrument {{Sets}}',
    implants: 'Sterile Implants',
    editDelivery: 'Edit Delivery',
    saveChanges: 'Save Changes',
    receiveDelivered: 'Receive Delivered',
    deliveryUpdated: 'Delivery updated successfully',
    deliveryCheckOutCompleted: 'Check Out Completed',
    completeTurnaroundSets: 'Complete Turnaround {{Sets}}',
    exitTurnaround: 'Exit Turnaround',
    quickCheckOut: 'Quick Check Out',
    deleteDelivery: 'Delete',
    checkoutDelivery: 'Check Out the Delivery',
    checkOutMoreImplants: 'Check Out More Sterile Implants',
    missingRequiredFields: 'Surgery ID, Supplier, Surgeon, Surgery Date and Patient Initials are required',
    printDocuments: 'Print Documents',
    missingRequiredFieldsWarning: 'Missing required fields ({{numFields}}) — please complete to proceed.',
  },
  PrintTrayLabelButton: {
    printTrayLabels: 'Print Tray Labels',
    selectTrayLabels: 'Select Tray Labels to Print',
    printButton: 'Print',
    pleaseWait: 'Preparing Tray Labels to be printed. Please wait, this may take a while.',
    tray: 'Tray',
    universalTrayLabel: 'Universal Tray Label (No Tray Number)',
  },
  CheckOutTrayPage: {
    title: 'Check Out {{Set}}',
    deviceManufacturerPictureList: 'Device Manufacturer Picture List:',
    enlargeReferenceImage: 'Enlarge Reference Image',
    close: 'Close',
    ref: 'Ref',
    description: 'Description',
    qualityChecks: 'Quality Checks',
    qty: 'Qty',
    damaged: 'Damage',
    torque: 'Torque',
    debris: 'Debris',
    function: 'Function',
    noImage: 'No Image Provided',
  },
  CheckOutTraySubmitPage: {
    title: 'Tray Information',
    deviceManufacturerPictureList: 'Device Manufacturer Picture List:',
    enlargeReferenceImage: 'Enlarge Reference Image',
    ref: 'Ref',
    description: 'Description',
    qualityChecks: 'Quality Checks',
    qty: 'Qty',
    printAllTrayImages: 'Print All Tray Images',
    retakePhoto: 'Retake Photo',
    saving: 'Saving',
    savedTrayInspection: 'Saved tray inspection',
    continue: 'Continue',
    postImageCaptureInspection: 'Post Image Capture Inspection',
    typeAnyCommentsHere: 'Type any comments here ...',
  },
  versionLabel: {
    prefix: 'v',
  },
  NotifyCheckOutSingleSetModal: {
    title: 'Notify Supplier',
    subtitle: (supplierName: string) =>
      `Do you want to notify ${supplierName} that this single {{Set}} is ready for immediate collection now?`,
    goBack: 'Go Back',
    notNow: 'Not Now',
    checkOutAndNotify: 'Notify',
    loading: 'Sending...',
    success: 'Notification sent successfully',
  },
  ProceedSetsInspectionDialog: {
    title: 'Proceed to inspect remaining set(s)?',
    titleShowSetsMode: 'Please select one of the remaining {{Sets}} to inspect',
    cancel: 'Exit',
    submit: 'Proceed',
  },
  TrayImageCapture: {
    title: 'Capture Tray Image',
    captureImage: 'Capture Image',
    switchCamera: 'Switch Camera',
    noCamerasAvailable: 'No cameras available',
    cameraAccessHasBeenDenied: 'Camera access has been denied',
    cameraMissing: 'Camera missing',
    imageCaptureFailed: 'Failed to capture image',
    imageCaptured: 'Image captured',
  },
  DeliveryDetailEdit: {
    DigitalSignature: 'Digital Signature',
    orderNumber: 'Order number',
    deliveryNumber: 'Delivery number',
    surgeryReference: 'Surgery ID',
    digitalSignatureButtonText: 'Digital Signature',
    selectSupplier: 'Supplier',
    selectSurgeon: 'Surgeon',
    patientInitials: 'Patient Initials',
    surgeryDate: 'Surgery Date',
    noRoadCases: 'No. of Single Roadcases',
    noTubs: 'No. of Tubs',
    noBoxes: 'No. of Boxes',
    noPelicanCases: 'No. of Pelican Cases',
    noDoubleRoadCases: 'No. of Double Roadcases',
    numTraysReceived: 'No. of Trays Received',
    numTraysSentSterilising: 'No. of Trays Sent to Sterilising',
    content: 'Delivery Content',
    clinicalArea: 'Clinical Area',
    viewSurgery: 'View Surgery Information',
    signedForBy: 'Signed-for By',
    signedForAt: 'Signed-for At',
    shipper: 'Courier',
    shipmentId: 'Shipment ID',
    shippedItems: 'Shipped Items',
    shipmentEta: 'Shipment ETA',
  },
  EmptyState: {
    noResults: 'There are no items to display.',
  },
  Navigation: {
    help: 'Help',
    createSet: 'Create {{Set}}',
    createDevice: 'Create Device',
    logout: 'Logout',
    login: 'Login',
  },
  Routes: {
    ActionItemsV1: 'Action Items',
    ActionItems: 'Action Items',
    Home: 'Delivery List',
    PastDeliveries: 'Past Deliveries',
    PastSurgeries: 'Past Surgeries',
    AllOrders: 'All Orders',
    Reports: 'Reports',
    Report: 'Report',
    UserPortal: 'User Portal',
    CheckIn: 'Check In',
    CheckInWorkflow: 'Check In Workflow',
    ViewDispatch: 'View Dispatch',
    CaptureNonScannableImplants: 'Capture Non Scannable Sterile Implants',
    CaptureNonScannableSurgeryImplants: 'Capture Non Scannable Surgery Sterile Implants',
    CheckInTrayImageCapture: '',
    CheckInTrayDetails: '',
    CheckInTrayDecantImageCapture: '',
    CheckInTrayDecantDetails: '',
    CheckInTray: 'Check In Tray',
    CheckOut: 'Check Out',
    SetDetails: 'Instrument {{Set}} Details',
    CheckOutTray: 'Check Out Tray',
    CheckOutTrayImageCapture: '',
    CheckOutTraySubmit: 'Check Out Tray Submit',
    TrayImageCapture: 'Capture Tray Image',
    Orders: 'Orders',
    UploadOrders: 'Upload Orders',
    ReviewOrders: 'Review Orders',
    ConfirmInDock: 'Confirm In Dock',
    OrderV1: 'Order',
    Order: 'Order',
    OrderSet: 'OrderSet',
    OrderImplantList: 'Order Sterile Implant List',
    Settings: 'Settings',
    MergeSurgeons: 'Merge Surgeons',
    WorkflowSettings: 'Settings',
    AdvancedSettings: 'Settings',
    Locations: 'Settings',
    UnrecognisedSurgeons: 'Settings',
    Surgeons: 'Surgeons',
    Implants: 'Sterile Implants',
    SurgeryImplants: 'Surgery Sterile Implants',
    ImplantList: 'Sterile Implant List',
    SurgeryImplantList: 'Surgery Sterile Implant List',
    DeviceReps: 'Device Manufacturer Reps',
    Contacts: 'Contacts',
    LastTrayInspection: 'Last Supplier Tray Inspection',
    TrayPictureList: 'Tray Picture List',
    SurgeryList: 'Surgery List',
    Surgery: 'Surgery',
    Turnaround: 'Turnaround',
    TurnaroundList: 'Turnaround List',
    LoggedOut: 'Logged Out',
  },
  SettingsModal: {
    userDetails: 'User Details',
    user: 'User: ',
    roles: 'Roles: ',
    role: 'Role: ',
    tenant: 'Tenant: ',
    subscription: 'Subscription: ',
    switchUser: 'Switch User: ',
    differentAccount: 'Use a different account',
  },
  PinLogin: {
    keypad: { delete: 'Delete' },
    login: {
      title: 'Enter your PIN',
      description: ({ email }: { email: string }) => `Enter the 4-digit PIN for ${email}`,
      wrongPin: 'Incorrect PIN',
      lockedOut: ({ seconds }: { seconds?: number }) =>
        seconds
          ? `Too many attempts. Try again in ${Math.ceil(seconds / 60)} minute(s) or log in with your password.`
          : 'Too many attempts. Try again later or log in with your password.',
      error: 'Unable to log in with your PIN. Please try again or log in with your password.',
      lockedUntil: ({ time }: { time: string }) =>
        `Too many attempts. Try again after ${time}, or log in with your password.`,
      usePassword: 'Log in with password instead',
      forgotPin: 'Forgot PIN? Log in with password',
    },
    enrol: {
      setTitle: 'Set up PIN',
      changeTitle: 'Change PIN',
      enterPin: 'Choose a 4-digit PIN',
      confirmPin: 'Re-enter your PIN to confirm',
      mismatch: 'The PINs do not match. Please try again.',
      terms:
        'Your login will be stored encrypted so that you can sign in with this PIN on shared workstations at your location. Only enable this on devices you trust.',
      acceptTerms: 'I accept',
      next: 'Next',
      error: 'Unable to save your PIN. Please try again.',
      lockedOut: ({ seconds }: { seconds?: number }) =>
        seconds
          ? `Too many PIN attempts. Try again in ${Math.ceil(seconds / 60)} minute(s).`
          : 'Too many PIN attempts. Try again later.',
      saving: 'Saving PIN',
      success: 'PIN saved',
    },
    manage: {
      heading: 'PIN login: ',
      isSet: 'Set up',
      notSet: 'Not set up',
      setUp: 'Set up PIN',
      change: 'Change PIN',
      remove: 'Remove PIN',
      removed: 'PIN removed',
      removeError: 'Unable to remove your PIN. Please try again.',
      lockedUntil: ({ time }: { time: string }) =>
        `PIN setup is locked after too many attempts. Try again after ${time}.`,
    },
    offer: {
      title: 'Set up a new PIN?',
      body: 'Your old PIN was removed. Would you like to set a new one now?',
      later: 'Not now',
    },
  },
  SessionTimeout: {
    title: 'Are you still there?',
    description: ({ email, time }: { email: string; time: string }) =>
      `${email} has been inactive. You will be logged out in ${time}.`,
    enterPin: 'Enter your PIN to continue',
    usePin: 'Re-enter PIN',
    usePassword: 'Re-enter password',
    switchUser: 'Switch User',
    logOut: 'Log out',
    stayLoggedIn: 'Stay logged in',
    wrongPin: 'Incorrect PIN',
    error: 'Unable to verify. Please try again.',
  },
  ActiveUserBanner: {
    signedInAs: 'Signed in as',
    switchUser: 'Switch User',
  },
  Settings: {
    pageHeader: 'Settings',
    hideHistoricalDeliveries: 'Hide Historical Deliveries',
    hideHistoricalDeliveriesRecommendation: 'Recommend hiding deliveries with surgeries that occurred 3 days ago.',
    hideHistoricalDeliveriesSuccess: 'Updated hidden deliveries cut-off date',
    hideHistoricalDeliveriesError: 'Failed to update hidden deliveries cut-off date',
    menu: {
      general: 'General',
      mergeSurgeons: 'Merge Surgeons',
      locations: 'Locations',
      unrecognisedSurgeons: 'Unrecognised Surgeons',
      workflow: 'Workflow',
      advanced: 'Advanced',
    },
    barcodeType: 'Barcode Type',
    barcodeTypeSuccess: 'Updated barcode type',
    barcodeTypeError: 'Failed to update barcode type',
    barcodeTypeLabels: {
      code128: 'Code 128',
      itf: 'ITF',
      qrcode: 'QR Code',
      datamatrix: 'Data Matrix',
    },
    setTerminologySuccess: 'Updated Set/Caddy terminology',
    setTerminologyError: 'Failed to update Set/Caddy terminology',
    generalPage: {
      title: 'General Settings',
    },
    workflowPage: {
      title: 'Workflow Settings',
      allowSkipInspection: 'Allow Skip Inspection',
      setTerminology: 'What is a Set/Caddy known as?',
      takeTrayPhotos: {
        heading: 'Take tray photos',
        description: 'If on, the Check In and Check Out flows will offer the CSSD to capture an image of each tray.',
      },
      captureInspectionResults: {
        heading: 'Capture inspection results',
        description: 'If on, inspection results for each tray may be entered.',
      },
      nameSets: {
        heading: 'Name {{Sets}} and Trays',
        description:
          'If off, {{Set}} and Tray names will be discovered or generated, skipping the naming step in the Check In Flow.',
      },
      requireColor: {
        heading: 'Require colour at check-in',
        description: 'If on, the Check In Flow asks for a colour when the delivery has none.',
      },
      numberTrays: {
        heading: 'Number trays',
        description:
          'If off, trays will be automatically numbered sequentially, skipping the tray numbering step in the Check In Flow.',
      },
      enableSetConsolidation: {
        heading: 'Enable {{Set}} Consolidation',
        description: 'Toggle on to enable grouping of {{Sets}} under a single {{Set}}.',
      },
      setConsolidationMax: {
        heading: '{{Set}} Consolidation Max',
        description: 'The maximum number of {{Sets}} that can be grouped.',
      },
      addingOwnSterilisationBarcodes: 'Adding own sterilisation barcodes enabled',
    },
    advancedPage: {
      title: 'Advanced Settings',
      legalNoticeAgree: 'I agree',
      legalNoticeDecline: 'Cancel',
      forceStoragePassthrough: {
        heading: 'Force Firebase Storage requests to use the SAVI API passthrough',
      },
      rememberLoginOrg: {
        heading: 'Remember the login organisation',
        description: 'Select this for this device if you always log in as the same organisation.',
      },
      quickSwitcher: {
        heading: 'Enable the user quick switcher',
        description: "Enabling this will allow your users to see each other's account details including Email Address.",
        forLocationLabel: 'Enable feature for all devices',
        browserLabel: 'Use quick switcher on this device',
        legalNotice: {
          title: 'Enable the user quick switcher',
          body: "By enabling this feature, your users' email addresses for this location will be cached on the local machine so they can be quickly selected at login. Only enable this on devices you trust.",
        },
      },
      pinLogin: {
        heading: 'Enable PIN login',
        description:
          'Allows enrolled users to log in on a trusted, shared device using a short PIN instead of their full credentials.',
        forLocationLabel: 'Enable feature for all devices',
        browserLabel: 'Use PIN login on this device',
        disableConfirm: {
          title: 'Disable PIN login',
          body: 'PIN login will be turned off for everyone at this location and all saved PINs will be removed. Users will need to set up their PIN again if you turn it back on.',
          confirmLabel: 'Disable',
        },
        legalNotice: {
          title: 'Enable PIN login',
          body: "By enabling this feature, your users' email addresses for this location will be cached on the local machine, and encrypted authentication data will be cached locally and in a hosted keystore to allow PIN login. Only enable this on devices you trust.",
        },
      },
      sessionTimeout: {
        heading: 'Enable session timeout',
        description: 'Automatically logs out idle users after a period of inactivity.',
      },
      sessionTimeoutIdleMins: {
        heading: 'Idle timeout (minutes)',
      },
      sessionTimeoutWarningDurationMins: {
        heading: 'Timeout warning duration (minutes)',
      },
    },
    locationsPage: {
      title: 'Hospitals and suppliers',
      description:
        'Maps hospital names used in supplier order files to the master hospital, and links Savi locations to SmartWays location IDs.',
      tabs: { hospitals: 'Hospitals', suppliers: 'Suppliers' },
      notLinked: 'Not linked to SmartWays',
      swUnavailable: 'SmartWays links unavailable',
      swLoading: 'Loading SmartWays links…',
      linkSw: 'Link SW',
      linkSwFor: 'Link SmartWays location to {{location}}',
      loadError: 'Could not load locations',
      hospitals: {
        onlyWithAliases: 'Only hospitals with aliases',
        addAliasFor: 'Add alias for {{location}}',
        searchPlaceholder: 'Search hospitals',
      },
      suppliers: {
        searchPlaceholder: 'Search suppliers',
        onlyLinked: 'Only linked',
        locationCount: '{{count}} locations',
        locationCountOne: '1 location',
        summary: '{{locations}} · {{links}}',
        linkedCount: '{{count}} linked',
        linksUnavailable: 'links unavailable',
        aliasesFor: 'View {{count}} aliases for {{supplier}}',
        aliasesForOne: 'View 1 alias for {{supplier}}',
        empty: 'No supplier locations found',
        columns: { location: 'Supplier location', smartways: 'SmartWays', aliases: 'Aliases', actions: 'Actions' },
      },
      linkDialog: {
        title: 'Link SmartWays location',
        swLocationId: 'SmartWays location ID',
        swUnavailable: 'SmartWays links unavailable, cannot check whether this ID is already linked',
        currentlyLinked: 'Currently linked to {{location}}. Linking will move it.',
        link: 'Link',
        linkedSuccess: 'Linked SmartWays location',
        alreadyLinked: 'SmartWays location already linked',
      },
      unlinkDialog: {
        title: 'Unlink SmartWays location',
        body: 'Unlink SmartWays location {{sw}} from {{location}}?',
        confirm: 'Unlink',
        chipLabel: 'Unlink SmartWays location {{sw}}',
        chipAriaLabel: '{{label}}, unlink SmartWays location {{sw}}',
        success: 'Unlinked SmartWays location',
      },
    },
    hospitalAliasesPage: {
      supplierFilter: 'Supplier',
      allSuppliers: 'All suppliers',
      addAlias: 'Add alias',
      empty: 'No hospital aliases found',
      loadError: 'Could not load hospital aliases',
      columns: {
        supplier: 'Master hospital',
        supplierCaption: 'Suppliers with an alias for it are listed beneath',
        alias: "Supplier's name for hospital (alias)",
        aliasCaption: 'As written in their order files',
        created: 'Created',
        actions: 'Actions',
      },
      edit: 'Edit alias',
      delete: 'Delete alias',
      dialog: {
        createTitle: 'Add hospital alias',
        editTitle: 'Edit hospital alias',
        supplierLocation: 'Supplier location',
        alias: "Supplier's name for the hospital",
        masterHospital: 'Master hospital',
        selectLocationPlaceholder: 'Select a location',
        save: 'Save',
      },
      deleteDialog: {
        title: 'Delete hospital alias',
        body: 'Delete the alias "{{alias}}" for {{hospital}}? Orders using this name will no longer match this hospital.',
        confirm: 'Delete',
      },
      createdSuccess: 'Hospital alias added',
      updatedSuccess: 'Hospital alias updated',
      deletedSuccess: 'Hospital alias deleted',
    },
    unrecognisedSurgeonsPage: {
      title: 'Unrecognised surgeons',
      description:
        "Surgeons created from incoming supplier orders (SmartWays and order uploads) that didn't match an existing surgeon. Merge each into the right surgeon, or confirm it's a new one.",
      searchPlaceholder: 'Search surgeon or hospital',
      empty: 'No unrecognised surgeons',
      loadError: 'Could not load unrecognised surgeons',
      columns: {
        entered: 'Entered name',
        enteredCaption: 'As sent in the order',
        best: 'Best match',
        actions: 'Actions',
      },
      noMatch: 'No match',
      score: 'Score {{score}}',
      badges: {
        dryRun: 'Dry run',
        wouldAutoLink: 'Would auto-link',
        severalMatches: 'Several close matches',
        initialsOnly: 'Initials only',
      },
      merge: 'Merge',
      confirmDistinct: 'Confirm new',
      mergeFor: 'Merge {{surgeon}}',
      confirmDistinctFor: 'Confirm {{surgeon}} is a new surgeon',
      mergeDialog: {
        title: 'Merge surgeon',
        heading: '{{surgeon}} at {{hospital}}',
        target: 'Merge into',
        selectTarget: 'Select a surgeon',
        bestMatch: '{{surgeon}} (best match, {{score}})',
        initialsWarning:
          'This name is only an initial and surname, so it could be more than one surgeon. Check the order before merging.',
        warning:
          "Moves this surgeon's orders and surgeries to the selected surgeon, then deletes it. The entered name is saved as an alias so future orders match automatically. This can't be undone.",
        loadError: "Could not load this hospital's surgeons",
        bestNotTarget:
          "{{surgeon}} can't be merged into, as it's also unrecognised or no longer active at this hospital.",
        confirm: 'Merge',
        success: 'Merged into {{surgeon}}',
        aliasNotSaved:
          "Merged, but the name wasn't saved as an alias, so future orders with this name won't match automatically",
      },
      confirmDialog: {
        title: 'Confirm new surgeon',
        body: 'Confirm {{surgeon}} at {{hospital}} is a new surgeon? It stays as is and is removed from this list.',
        confirm: 'Confirm new',
        success: 'Confirmed {{surgeon}} as a new surgeon',
      },
    },
  },
  Dialog: {
    Close: 'Close',
    Cancel: 'Cancel',
    Confirm: 'Confirm',
    OK: 'OK',
    YesExit: 'Yes, Exit',
  },
  EditDueDateDialog: {
    title: 'Update Return Date',
    heading: "Updating 'Return Date' for delivery {deliveryNumber}",
    UpdateSuccess: "Updated 'Return Date' for delivery {deliveryNumber}",
    UpdateError: "Failed to update 'Return Date' for delivery {deliveryNumber}",
  },
  ConfirmModal: {
    confirm: 'Confirm',
    delete: 'Delete',
    tray: 'Are you sure you want to delete this tray?',
    device: 'Are you sure you want to delete this device?',
  },
  UserManagement: {
    name: 'Name',
    email: 'Email',
    role: 'Role',
    location: 'Location',
    active: 'Active',
    inactive: 'Inactive',
    organisation: 'Organisation',
    provider: 'Auth Provider',
    delete: 'Delete',
    edit: 'Edit',
    none: 'None',
    adminRole: 'Savi Admin',
    error: 'User management change error: ',
    searchPlaceholder: 'Enter email',
    showInactiveUsers: 'Show Inactive Users',
    DeleteModal: {
      title: 'Delete User',
      placeholder: `Type the user's email`,
      submit: 'Delete',
      body: `Are you sure you want to delete {{email}}? Type their email below to confirm.`,
      errorMessage: 'Could not delete user: ',
      successMessage: 'Successfully deleted user',
    },
    createUser: 'Create User',
    CreateModal: {
      title: 'Create User',
      email: 'Email*',
      emailPlaceholder: `Type the user's email`,
      role: 'Role*',
      organisation: 'Organisation',
      location: 'Location*',
      provider: 'Provider',
      submit: 'Create',
      body: `Please fill out the new user account's details below`,
      errorMessage: 'Could not create user: ',
      successMessage: 'Successfully created user',
    },
    EditModal: {
      title: 'Edit User',
      role: 'Role*',
      organisation: 'Organisation',
      location: 'Location*',
      provider: 'Provider',
      active: 'Active',
      submit: 'Save',
      body: `Update {{email}}'s account details below`,
      errorMessage: 'Could not update user: ',
      successMessage: 'Successfully updated user',
    },
  },
  Header: {
    searchPlaceholder: 'Search',
    searchNoResults: 'No results found',
  },
  ReferenceImageModal: {
    title: 'Reference Image Generator',
    addTag: 'Add New Tag',
    removeTag: 'Remove Last Tag',
    download: 'Download',
    saveAndSubmit: 'Save & Submit',
    exit: 'Exit',
    imageLoadError: 'Error loading image',
    imageDownloadError: 'Failed to download image',
    imageSaveError: 'Failed to save image',
  },
  SearchInput: {
    placeholder: 'Search',
    searchOrScanPlaceholder: 'Search on anything or scan Smartways Shipment ID',
    buttonLabel: 'Search',
  },
  SearchFilters: {
    buttonLabel: 'Filters',
  },
  DeliveryStatusText: {
    DISPATCHED: 'Dispatched',
    OUTBOUND: 'Dispatched',
    DELIVERED: 'Delivered to Hospital', //DELIVERED - supplier view; hospital view shows 'Delivered' via DELIVERED_HOSPITAL
    DELIVERED_HOSPITAL: 'Delivered', //DELIVERED - when viewed by a hospital user
    RECEIVED: 'Received',
    PARTIALLY_CHECKED_IN: 'Partially Checked In',
    CHECKED_IN: 'Checked In',
    PARTIALLY_CHECKED_OUT: 'Partially Checked Out',
    CHECKED_OUT: 'Checked Out',
    PARTIALLY_RETURNED: 'Partially Returned',
    RETURNED: 'Returned',
    DELETED: 'Deleted',
    CANCELED: 'Cancelled',
  },
  DeliveryContent: {
    [DeliveryContent.Default]: 'No content type specified',
    [DeliveryContent.Instruments]: 'Instrument {{Sets}}',
    [DeliveryContent.Implants]: 'Sterile Implants',
    [DeliveryContent.InstrumentsAndImplants]: 'Instrument {{Sets}} & Sterile Implants',
  },
  DeliveryContentType: {
    [DeliveryContentType.DMInstrumentSets]: 'Instrument {{Sets}}',
    [DeliveryContentType.SterileImplants]: 'Sterile Implants',
  },
  DeliverySetButton: {
    viewInspection: 'View Inspection',
    sendReadinessNotification: 'Notify Supplier {{Set}} is Ready for Collection',
    deleteSet: 'Delete {{Set}}',
    deselectTurnaround: 'Deselect turnaround {{Set}}',
    turnaroundSet: 'Turnaround {{Set}}',
    quickCheckedOutReasons: {
      [DeliveryQuickCheckOutReason.WithoutSaviCapture]: 'Quick Checked Out without',
      [DeliveryQuickCheckOutReason.SalesRep]: 'Quick Checked Out by Sales Rep',
    },
    failedToUpdateSetName: 'Failed to update instrument {{Set}} name.',
    setNameRequired: 'Instrument {{Set}} name is required. Resetting to original value.',
    setNameExists: ({ newDisplayCode, originalDisplayCode }: { newDisplayCode: string; originalDisplayCode: string }) =>
      `Instrument {{Set}} name exists: ${newDisplayCode}. Resetting to original value: ${originalDisplayCode}.`,
    turnaroundOrigin: 'Turnaround',
    turnaroundDestination: 'Turnaround',
  },
  Shared: {
    goBack: 'Go Back',
    // used to identify a delivery whose number was auto generated and so is not shown
    deliveryFallbackLabel: 'Delivery {number}',
  },
  OrdersPage: {
    title: 'Orders',
    uploadOrdersButton: 'Upload Orders',
    confirmInDockButton: 'Confirm In Dock',
    filterColumnsButton: 'Filter Columns',
    allStatusesPlaceholderText: 'All Statuses',
    columns: {
      [OrdersColumnId.Hospital]: 'Hospital',
      [OrdersColumnId.OrderNumber]: 'Order Number',
      [OrdersColumnId.DeliveryNumber]: 'Delivery Number/s',
      [OrdersColumnId.SurgeryDate]: 'Surgery Date',
      [OrdersColumnId.DueDate]: 'Estimated Check Out Date',
      [OrdersColumnId.Status]: 'Status',
      [OrdersColumnId.Express]: 'Express',
      [OrdersColumnId.DeviceMissing]: 'Missing Devices?',
    },
    express: 'Express',
    viewInspectionResults: 'View Inspection Results',
    expressReturn: 'Express Return',
    successExpressDeliveryMessage: 'Delivery marked as Express',
  },
  AllOrdersPage: {
    title: 'All Orders',
    allHospitalsPlaceholderText: 'All Hospitals',
    hospitalFilterLabel: 'Hospital',
    supplierFilterLabel: 'Supplier',
    allSuppliersPlaceholderText: 'All Suppliers',
    surgeryDateFilter: 'Surgery Date',
    table: {
      status: 'Status',
      surgeryDate: 'Surgery Date',
      hospital: 'Hospital',
      supplier: 'Supplier',
      orderNumber: 'Order Number',
      deliveryNumber: 'Delivery Number/s',
      shipmentId: 'Shipment ID/s',
      surgeon: 'Surgeon',
    },
  },
  UploadOrdersPage: {
    title: 'Upload orders/dispatches',
    downloadTemplateLabel: 'Format',
    downloadTemplateDescription: 'Download the below template to upload your files.',
    downloadTemplateButton: 'Template Download',
    inputTitle: 'Click or drag file to this area to upload',
    inputDescription:
      'Support for a single or bulk upload. Strictly prohibit from uploading company data or other band files',
    loading: 'Uploading...',
    validating: 'Validating...',
    validatingMany: 'Validating (May take several minutes)...',
    error: 'Error. Please try again.',
    errorFileSize: 'File size must be less than',
    errorFileType: 'Please upload a CSV file',
    errorCsvRows: 'CSV rows must not exceed',
    success: 'Orders uploaded successfully',
  },
  ReviewOrdersPage: {
    title: 'Review Orders',
    proceed: 'Proceed',
    filename: 'Filename',
    instructions: 'Please review the orders below and confirm they are correct.',
    tabs: {
      approve: 'APPROVE',
      reviewRequired: 'REVIEW REQUIRED',
      rejected: 'REJECTED',
    },
    processing: 'Processing (May take several minutes)...',
    createOrdersSuccess: 'Orders created successfully',
  },
  ConfirmInDockPage: {
    title: 'Confirm Returned Goods',
    backToOrders: 'Back to Orders',
    clearAll: 'Clear All',
    scanInputLabel: 'Scan or enter barcode',
    scanInputPlaceholder: 'Click to scan or enter a barcode',
    recentErrors: 'Recent Errors',
    columns: {
      status: 'Status',
      barcode: 'Barcode',
      itemName: 'Item Name',
      orderNumber: 'Order Number',
      hospital: 'Hospital',
      timestamp: 'Timestamp',
      actions: 'Actions',
    },
    statusSuccess: 'IN DOCK',
    statusError: 'ERROR',
    confirm: 'Confirm',
    duplicateError: 'Item already scanned',
    alreadyReturnedError: 'Item has already been returned',
    unknownError: 'Unknown error',
    confirmFailedError: 'Failed to confirm return',
    removeItem: 'Remove',
    confirmDialog: {
      title: 'Confirm Return',
      body: 'You are about to confirm {{count}} {{items}} as returned. Do you want to proceed?',
      confirm: 'Yes, confirm',
      cancel: 'Cancel',
    },
  },
  ReviewOrdersApprove: {
    ignore: 'Ignore',
    columns: {
      actions: 'Actions',
      orderNumber: 'Order Number',
      deliveryNumber: 'Delivery Number',
      setCode: '{{Set}} Code',
      uniqueSerialCode: 'Serial',
      surgeryDate: 'Surgery Date',
      hospital: 'Hospital',
      surgeon: 'Surgeon',
    },
  },
  ReviewOrdersReviewRequired: {
    ignore: 'Ignore',
    merge: 'Merge',
    attachSerialToSet: 'Attach',
    columns: {
      actions: 'Actions',
      orderNumber: 'Order Number',
      deliveryNumber: 'Delivery Number',
      setCode: '{{Set}} Code',
      uniqueSerialCode: 'Serial',
      surgeryDate: 'Surgery Date',
      hospital: 'Hospital',
      surgeon: 'Surgeon',
      reason: 'Reason',
    },
    selectHospital: 'Please select a hospital',
    selectSurgeon: 'Please select a surgeon',
    create: 'Create',
    createNew: 'Create New',
    hospitalUpdatedSuccess: `Hospital updated successfully`,
    surgeonUpdatedSuccess: `Surgeon updated successfully`,
  },
  ReviewOrdersRejected: {
    addBack: 'Add Back',
    columns: {
      actions: 'Actions',
      orderNumber: 'Order Number',
      deliveryNumber: 'Delivery Number',
      setCode: '{{Set}} Code',
      uniqueSerialCode: 'Serial',
      surgeryDate: 'Surgery Date',
      hospital: 'Hospital',
      surgeon: 'Surgeon',
      reason: 'Reason',
    },
    addBackSuccess: 'Order added back successfully',
  },
  IgnoreOrdersDialog: {
    title: 'Ignore Order',
    subtitle: 'Are you sure you want to ignore this order?',
    cancel: 'Cancel',
    confirm: 'Yes, ignore',
  },
  MergeOrderDialog: {
    title: 'Merge Order',
    subtitle: 'Are you sure you want to merge this order?',
    existingOrder: 'Existing Order',
    updatedOrder: 'Updated Order',
    orderNumber: 'Order Number',
    orderStatus: 'Order Status',
    surgeonName: 'Surgeon Name',
    surgeryDate: 'Surgery Date',
    surgeryReference: 'Surgery ID',
    clinicalArea: 'Clinical Area',
    cancel: 'Cancel',
    confirm: 'Yes, merge',
  },
  ReceiveDeliveryButton: {
    receiveDelivery: 'Receive Delivery',
  },
  ReceiveDeliveryDialog: {
    title: 'Receive Delivery',
    headings: {
      search: 'Please provide the delivery information below:',
      deliveries: 'Please select from the below list of dispatched deliveries:',
      surgeries: 'If the surgery is listed below please select it, else Create a New Surgery',
      save: 'Please provide the delivery details below:',
    },
    helperText: 'Reminder: Fields with asterisks (*) are mandatory fields.',
    formLabels: {
      receivedDate: 'Received Date',
      supplier: 'Supplier',
      supplierRep: 'Device Manufacturer Representative',
      orderNumber: 'Order No.',
      deliveryNumber: 'Delivery No.',
      patientInitials: 'Patient Initials',
      surgeon: 'Surgeon',
      surgeryDate: 'Surgery Date',
      deliveryContents: 'Delivery Contents',
      surgeryReference: 'Surgery ID',
      shipmentDetails: 'Shipment Details',
      numRoadCases: 'No. of Single Roadcases',
      numTubs: 'No. of Tubs',
      numBoxes: 'No. of Boxes',
      numPelicanCases: 'No. of Pelican Cases',
      numDoubleRoadCases: 'No. of Double Roadcases',
    },
    ordersTable: {
      surgeryReference: 'Surgery ID',
      orderNumber: 'Order No.',
      deliveryNumber: 'Delivery No.',
      supplierName: 'Supplier',
      surgeonName: 'Surgeon',
      surgeryDate: 'Surgery Date',
      surgeryDescription: 'Description',
      surgeryComments: 'Internal Notes',
      patientInitials: 'Patient Initials',
    },
    noResultsFound: 'No results found from {supplierName} on {surgeryDate} for {surgeonName}',
    confirm: 'Complete Receive',
    continue: 'Continue',
    selectDeliveryAbove: 'Select a delivery above',
    receiveSelectedDelivery: 'Receive the Selected Delivery',
    deliveryNotFound: 'If the delivery is not listed click here',
    selectSurgeryAbove: 'Select an existing surgery above',
    attachToSelectedSurgery: 'Attach to the Selected Surgery',
    createNewSurgery: 'Create a New Surgery',
    completeReceive: 'Complete Receive',
    success: 'Delivery received successfully',
    unsavedChangesDialog: {
      title: 'Unsaved Changes',
      heading: 'You have unsaved changes. Are you sure you want to exit?',
      confirm: 'Yes, exit',
      cancel: 'Cancel',
    },
    deliveryExistsDialog: {
      title: 'Delivery Already Exists',
      heading: 'The delivery that you are trying to create already exists. Do you want to open it?',
      confirm: 'Yes',
      cancel: 'No',
    },
    existingSurgeryId: 'Existing Surgery ID',
    existingOrderNumber:
      'Order number already linked to an existing surgery, please check surgery details entered on first modal or change the entered number above.',
  },
  ReceiveDeliverySuccessDialog: {
    title: 'Your delivery has been received',
    generateReceivedDocument: 'Generate Received Document',
    checkInNow: 'Check in this delivery now',
    close: 'Close',
  },
  PrintDocumentsModal: {
    title: 'Print Documents',
    close: 'Close',
    printReceivedDeliveryNote: 'Print Received Delivery Note',
    printCheckoutDeliveryNote: 'Print Check Out Delivery Note',
    printAllTrayImages: 'Print Delivery Information',
    printTrayLabels: 'Print Tray Labels',
    selectDeliveries: 'Select Deliveries',
    confirm: 'Confirm',
  },
  OrderStatusText: {
    Default: 'Ordered',
    ORDERED: 'Ordered', //ORDERED - order has been placed but not yet dispatched
    PARTIALLY_DISPATCHED: 'Partially Dispatched', //PARTIALLY_DISPATCHED - some items dispatched
    OUTBOUND: 'Dispatched', //OUTBOUND - dispatched and on the way to the hospital
    INBOUND: 'Inbound', //INBOUND - on the way back to the supplier
    PARTIALLY_DELIVERED: 'Partially Delivered', //PARTIALLY_DELIVERED - some items delivered to the hospital
    DELIVERED: 'Delivered to Hospital', //DELIVERED - delivered to the hospital; hospital view shows 'Delivered' via DELIVERED_HOSPITAL
    RECEIVE: 'Received by Hospital', //RECEIVE - received by the hospital
    RECEIVE_HOSPITAL: 'Received', //RECEIVE - when viewed by a hospital user
    INSPECTION: 'Inspection', //INSPECTION - being inspected in the hospital
    PARTIALLY_RECEIVED: 'Partially Received', //PARTIALLY_RECEIVED - partially received at the hospital
    CHECK_IN: 'Checked In', //CHECK_IN - checked into the hospital
    CHECK_OUT: 'Checked Out', //CHECK_OUT - checked out of the hospital
    PARTIALLY_CHECKED_IN: 'Partially Checked In', //PARTIALLY_CHECKED_IN - partially checked in to the hospital
    PARTIALLY_CHECKED_OUT: 'Partially Checked Out', //PARTIALLY_CHECKED_OUT - partially checked out of the hospital
    DELETED: 'Deleted', //DELETED - deleted from the system
    CANCELED: 'Cancelled',
  },
  DeviceRepSpecialtyText: {
    orthopedics: 'Orthopedics',
    spine: 'Spine',
    trauma: 'Trauma',
    earNoseThroat: 'Ear Nose Throat',
    plasticAndReconstructive: 'Plastic & Reconstructive',
    sportsMedicine: 'Sports Medicine',
    generalSurgery: 'General Surgery',
    oncology: 'Oncology',
    hip: 'Hip',
    knee: 'Knee',
    shoulder: 'Shoulder',
    elbow: 'Elbow',
    hand: 'Hand',
    footAndAnkle: 'Foot and Ankle',
    pediatricOrthopedics: 'Pediatric Orthopedics',
  },
  ConfirmTurnaroundSetsDialog: {
    title: 'Confirm Turnaround {{Sets}}',
    subtitle: 'If all {{Sets}} required to be turnaround are selected, select Proceed, if not go back.',
    options: {
      cancel: 'No, go back',
      confirm: 'Proceed',
    },
  },
  ReceiveDialog: {
    title: 'Receive this delivery',
    subtitle: 'Are you sure you want to receive this delivery?',
    actions: {
      cancel: 'Cancel',
      confirm: 'Receive',
    },
  },
  DeliveryExistsDialog: {
    title: 'This Delivery Already Exists',
    subtitle: 'Do you want to open the delivery?',
    actions: {
      cancel: 'Cancel',
      confirm: 'Continue to check-in',
    },
  },
  SurgeryPage: {
    title: 'Surgery Information',
    editSurgery: 'Edit Surgery',
    saveSurgery: 'Save Changes',
    checkOut: 'Check Out',
    headings: {
      surgery: 'Surgery',
      connectedDeliveries: 'Connected Deliveries',
      sets: 'Instrument {{Sets}}',
      implants: 'Sterile Implants',
      attachments: 'Attachments',
    },
    sets: 'Instrument {{Sets}}',
    implants: 'Sterile Implants',
    noSets: 'No {{Sets}} found',
    noImplants: 'No sterile implants found',
    deliveriesTableHeaders: {
      status: 'Status',
      orderNumber: 'Order No.',
      deliveryNumber: 'Delivery No.',
      shipmentId: 'Shipment ID',
      shipper: 'Courier',
      deliveryPriority: 'Priority Request',
      deliveryContents: 'Contents',
    },
    missingRequiredFields: 'Surgery ID, Supplier, Surgeon, Surgery Date and Patient Initials are required',
    failedToUpdateSurgery: 'Failed to update surgery',
    printAllSurgeryInformation: 'Print Surgery Information',
    printDocuments: 'Print Documents',
    surgeryUpdated: 'Surgery updated successfully',
  },
  ViewEditSurgeryForm: {
    formLabels: {
      orderNumber: 'Order Number',
      surgeryReference: 'Surgery ID',
      supplier: 'Supplier',
      surgeon: 'Surgeon',
      surgeryDate: 'Surgery Date',
      colorCode: 'Colour',
      surgeryComments: 'Internal Notes',
      patientInitials: 'Patient Initials',
      surgeryStatus: 'Surgery Status',
      supplierRep: 'Device Manufacturer Representative',
      createdBy: 'Created By',
      surgeryDescription: 'Surgery Description',
      signedForBy: 'Signed-for By',
      signedForAt: 'Signed-for At',
    },
    statuses: {
      REQUESTED: 'Booking requested from the Device Manufacturer',
      BOOKED: 'Already booked with the Device Manufacturer',
    },
    existingSurgeryId: 'Existing Surgery ID',
  },
  ViewEditOrderForm: {
    formLabels: {
      orderNumber: 'Order Number',
      surgeryReference: 'Surgery ID',
      hospital: 'Hospital',
      surgeon: 'Surgeon',
      surgeryDate: 'Surgery Date',
      colorCode: 'Colour',
      surgeryComments: 'Internal Notes',
      patientInitials: 'Patient Initials',
      surgeryStatus: 'Surgery Status',
      supplierRep: 'Device Manufacturer Representative',
      createdBy: 'Created By',
      surgeryDescription: 'Surgery Description',
      signedForBy: 'Signed-for By',
      signedForAt: 'Signed-for At',
    },
    statuses: {
      REQUESTED: 'Booking requested from the Device Manufacturer',
      BOOKED: 'Already booked with the Device Manufacturer',
    },
    existingSurgeryId: 'Existing Surgery ID',
  },
  OrderPageV1: {
    order: 'Order',
    surgery: 'Surgery',
    notFound: 'Not Found',
    orderInformation: 'Order Information',
    orderInformationBlankStates: {
      supplierName: 'No supplier name set',
      clinicalArea: 'No clinical area set',
      surgeonName: 'No surgeon name set',
      surgeryDate: 'No surgery date set',
      deliveryContent: 'No delivery content set',
      surgeryReference: 'No Surgery ID set',
    },
    shipperDetails: 'Shipment Details',
    shipperDetailsTitles: {
      roadCases: 'No. of Road Cases',
      tubs: 'No. of Tubs',
      boxes: 'No. of Boxes',
      traysReceived: 'No. of Trays Received',
      traysSentSterilising: 'No. of Trays Sent to Sterilising',
    },
    deliveryNumbers: 'Delivery Number/s',
    noDeliveries: 'No deliveries found',
    sets: 'Instrument {{Sets}}',
    noSets: 'No {{Sets}} found',
    completeTurnaroundSets: 'Complete Turnaround {{Sets}}',
    exitTurnaround: 'Exit Turnaround',
  },
  OrderPage: {
    title: 'Order Information',
    headings: {
      order: 'Order',
      connectedDeliveries: 'Connected Deliveries',
      sets: 'Instrument {{Sets}}',
      implants: 'Sterile Implants',
    },
    sets: 'Instrument {{Sets}}',
    implants: 'Sterile Implants',
    noSets: 'No {{Sets}} found',
    noImplants: 'No sterile implants found',
    deliveriesTableHeaders: {
      status: 'Status',
      orderNumber: 'Order No.',
      deliveryNumber: 'Delivery No.',
      deliveryPriority: 'Priority Request',
      deliveryContents: 'Contents',
    },
    expressReturnSets: 'Express Return {{Sets}}',
    turnaroundSets: 'Turnaround {{Sets}}',
    selectTurnaroundSets: 'Select Turnaround {{Sets}}',
    continue: 'Continue',
    addTurnaroundSetsToOrderSuccess: '{{Sets}} added to order successfully',
  },
  OrderSetButton: {
    turnaroundSet: 'Turnaround {{Set}}',
    viewInspectionResults: 'View Inspection Results',
    express: 'Express',
    expressReturn: 'Express Return',
    removeExpressReturn: 'Remove Express Return',
    expressReturnSuccess: '{{Set}} marked as Express Return',
    removeExpressReturnSuccess: '{{Set}} Express Return removed',
  },
  OrderSetPage: {
    set: '{{Set}}',
    setDetails: 'Instrument {{Set}} Details',
    setDetailsTitles: {
      traysReceived: 'No. of Trays Received',
      traysSentSterilising: 'No. of Trays Sent to Sterilising',
    },
    checkIn: 'Check In',
    checkOut: 'Check Out',
  },
  MergeSurgeonsPage: {
    title: 'Surgeon Admin',
    subtitle: 'Manage surgeons, hospitals, and name aliases',
    boxTitle: 'Merge Duplicate Surgeons',
    boxSubtitle:
      'Select 2 or more surgeons to merge into one record. The merge-into surgeon record must be locked. All aliases will be migrated to the kept surgeon.',
    searchTitle: 'Search and Add Surgeons to Merge',
    searchPlaceholder: 'Search by name or APHRA number...',
    searchNotFound: 'No surgeons found for "{{searchTerm}}".',
    selectedTitle: 'Selected Surgeons ({{count}})',
    keeperInstruction: 'Which surgeon record should be kept?',
    keeperSubInstruction:
      "You must select a locked surgeon. The other surgeons' names will become aliases for the kept surgeon, and all their aliases will be transferred.",
    dryRunLabel: 'Perform a dry run (no changes will be made)',
    mergeButton: 'Merge {{count}} Surgeons',
    mergeButtonDisabled: 'Select at least 2 surgeons and choose which to keep',
    confirmMergeTitle: 'Confirm Merge',
    confirmMergeWarning: 'This merge cannot be undone. Are you sure?',
    confirmMergeProceed: 'Confirm Merge',
    surgeriesFound: '{{count}} surgeries found for the surgeons being merged.',
    noSurgeriesFound: 'No surgeries found for the surgeons being merged.',
    mergeSuccess: 'Surgeons merged successfully.',
    mergeError: 'An error occurred while merging surgeons.',
    actions: {
      select: 'Select',
      cancel: 'Cancel',
    },
  },
  SurgeonsPage: {
    addSurgeon: 'Add',
    columns: {
      [SurgeonsColumnId.SurgeonName]: 'Surgeon',
      [SurgeonsColumnId.Specialty]: 'Specialty',
      [SurgeonsColumnId.ContactNumbers]: 'Contact',
      [SurgeonsColumnId.EmailAddresses]: 'Email',
      [SurgeonsColumnId.Verification]: 'Verification',
      [SurgeonsColumnId.Status]: 'Status',
      [SurgeonsColumnId.Actions]: '',
    },
    surgeonsFilterOpts: {
      verified: 'Verified Surgeons',
      unverified: 'Unverified Surgeons',
      active: 'Active Surgeons',
      inactive: 'Inactive Surgeons',
      all: 'All Surgeons',
    },
  },
  DeviceRepsPage: {
    addDeviceRep: 'Add',
    columns: {
      [DeviceRepsColumnId.DeviceRepName]: 'DM Reps Name',
      [DeviceRepsColumnId.Specialty]: 'Specialty',
      [DeviceRepsColumnId.EmailAddresses]: 'Email Address',
      [DeviceRepsColumnId.ContactNumbers]: 'Contact Number',
      [DeviceRepsColumnId.SupplierTenantName]: 'Company',
      [DeviceRepsColumnId.Status]: 'Status',
      [DeviceRepsColumnId.Actions]: '',
    },
    allSpecialtiesPlaceholderText: 'All Specialties',
  },
  EditSurgeonDialog: {
    searchSurgeon: 'Add Surgeon - Search Existing',
    addSurgeon: 'Add Surgeon',
    editSurgeon: 'Edit Surgeon',
    viewSurgeon: 'View Surgeon',
    surgeonName: 'Surgeon Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    title: 'Title',
    licenseNumber: 'AHPRA Registration Number',
    licenseNumberValidation: 'Format: MED followed by 10 numbers (e.g. MED1234567890)',
    licenseNumberLookup: 'Lookup AHPRA Number',
    specialties: 'Specialties',
    active: 'Active',
    name: 'Name',
    actions: {
      close: 'Close',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
    },
    createSurgeonSuccess: 'Surgeon created successfully',
    editSurgeonSuccess: 'Surgeon updated successfully',
    notes: 'Notes',
    primaryEmailAddress: 'Primary Email Address',
    secondaryEmailAddress: 'Secondary Email Address',
    primaryContactNumber: 'Primary Contact Number',
    secondaryContactNumber: 'Secondary Contact Number',
    none: 'None',
    notSpecified: 'Not Specified',
    enterSurgeonName: 'Enter surgeon name to check if they already exist',
    noResults: "Not finding who you're looking for?",
    alreadyInHospital: 'Already in your hospital',
    surgeonExists: 'This surgeon is already in your hospital',
    availableSurgeons: 'Available surgeons (click to add)',
    notFound: 'No surgeons found matching "{{searchTerm}}"',
    addNewSurgeon: 'Add a New Surgeon',
    surgeonAddedToHospital: 'The surgeon was added to your hospital',
    surgeonAddError: 'Failed to add the surgeon to your hospital',
  },
  OrderTurnaroundOptionsDialog: {
    title: 'Turnaround Options',
    subtitle: 'Turnaround into an existing order?',
    options: {
      existingOrder: 'Existing Order',
    },
  },
  EditDeviceRepDialog: {
    viewDeviceRep: 'View DM Rep',
    addDeviceRep: 'Add DM Rep',
    editDeviceRep: 'Edit DM Rep',
    deviceRepName: 'DM Representative Name',
    deviceRepNamePlaceholder: 'Enter Device Rep Name',
    active: 'Active',
    actions: {
      cancel: 'Cancel',
      save: 'Save',
    },
    createDeviceRepSuccess: 'Device Rep created successfully',
    editDeviceRepSuccess: 'Device Rep updated successfully',
    note: 'Note',
    primaryEmailAddress: 'Primary Email Address',
    secondaryEmailAddress: 'Secondary Email Address',
    primaryContactNumber: 'Primary Contact Number',
    secondaryContactNumber: 'Secondary Contact Number',
    validationError: 'Please fill out all required fields',
  },
  FindExistingOrderDialog: {
    title: 'Find Existing Order',
    searchLabel: 'What order number are you looking for?',
    searchPlaceholder: 'Search Order Number',
    supplierLabel: 'What supplier are you looking for?',
    supplierPlaceholder: 'Select Supplier',
    surgeonLabel: 'What surgeon are you looking for?',
    surgeonPlaceholder: 'Select Surgeon',
    options: {
      cancel: 'Cancel',
      confirm: 'Search',
    },
    noOrders: 'No orders found. Please try different search criteria.',
  },
  ExpressReturnSetsDialog: {
    title: 'Select Instrument {{Sets}} to Express Return',
    options: {
      cancel: 'Cancel',
      confirm: 'Ok',
    },
    noSets: 'No Instrument {{Sets}} found',
    success: 'Instrument {{Sets}} marked as Express Return',
  },
  DeleteDeliveryDialog: {
    title: 'Are you sure you want to delete this delivery?',
    cancel: 'No',
    confirm: 'Yes',
    deliveryDeleted: 'Deleted Delivery',
  },
  CancelSurgeryButton: {
    cancelSurgery: 'Cancel Surgery',
    dialogTitles: {
      cancelSurgeryConfirm: 'Do you want to skip the inspection and proceed to cancel this surgery?',
      notifySupplierConfirm: 'Do you want to send a notification to supplier that this surgery has been cancelled?',
    },
    successMessage: 'Surgery cancelled',
    errorMessage: 'Cancel surgery failed',
  },
  CancelDeliveryButton: {
    canceledSurgery: 'Cancelled Surgery',
  },
  CancelledSurgeryDialog: {
    title: 'Are you sure you want to cancel this delivery?',
    titleSurgery: 'Are you sure you want to cancel this surgery?',
    cancel: 'No',
    confirm: 'Yes',
    successMessage: 'Delivery cancelled successfully',
    successMessageSurgery: 'Surgery cancelled successfully',
  },
  NotifySupplierDeliveryCancelledDialog: {
    title: 'Do you want to notify the supplier that the {{Sets}} are ready for immediate collection?',
    cancel: 'No',
    confirm: 'Yes',
  },
  CreateSetsDialog: {
    title: 'How many Instrument {{Sets}} are in this delivery?',
    inputLabel: 'No. of Instrument {{Sets}}',
    maxSets: (maxSets: number) => `A maximum of ${maxSets} Instrument {{Sets}} is allowed`,
    confirm: 'Continue',
    setsCreated: 'Created Instrument {{Sets}} successfully',
    minSets: 'Please enter a number greater than 0',
  },
  SelectSetDialog: {
    title: {
      CHECK_IN: 'Please select the {{Set}} that you would like to check in',
      CHECK_OUT: 'Please select the {{Set}} that you would like to check out',
    },
    confirm: 'Continue',
    consolidate: 'Consolidate',
    consolidateTitle: {
      CHECK_IN: 'Please select the {{Sets}} that you would like to check in',
    },
    consolidationNoticeHeading: 'Consolidation',
    consolidationNotice: ({ max }: { max: number }) =>
      `Tick up to ${max} {{Sets}} to check them in together on one tray — one image capture and one inspection.`,
    consolidateSelected: ({ count }: { count: number }) => `Consolidate ${count} {{Sets}}`,
    confirmTitle: ({ count }: { count: number }) => `Check in these ${count} {{Sets}} on one tray?`,
    confirmDescription: 'A new {{Set}} holding one tray, numbered in the usual way at the end of check in.',
    consolidatedName: ({ n }: { n: number }) => `DM Consolidated {{Set}} ${n}`,
    consolidating: 'Consolidating, please wait',
    commentsHeading: 'Check In Comments will be pre-filled with',
    commentsPrefill: ({ names }: { names: string }) => `Consolidated {{Set}}. {{Sets}} on this tray: ${names}.`,
    cannotRemove: '{{Sets}} cannot be ungrouped after they are Checked In',
    back: 'Back',
    cancel: 'Cancel',
    error: 'Unable to consolidate {{Sets}}',
  },
  ConsolidatedSetGroup: {
    consolidated: 'Consolidated',
    setsOnThisTray: ({ count }: { count: number }) => `${count} {{Sets}} on this tray`,
    membersHeading: '{{Sets}} on this consolidated {{Set}}',
    ungroup: 'Ungroup',
    ungroupError: 'Unable to ungroup {{Sets}}',
  },
  SetNameDialog: {
    title: 'What is the name of this instrument {{Set}}?',
    inputLabel: 'Enter Instrument {{Set}} Name',
    confirm: 'Continue',
    nameRequiredError: 'No Instrument {{Set}} Name provided',
  },
  SelectColorDialog: {
    title: 'Select a colour',
    description: 'Assign a colour code for this surgery',
    label: 'Colour',
    confirm: 'Continue',
  },
  TrayNumberDialog: {
    title: 'What is the number for this Tray?',
    inputLabel: 'Tray Number',
    confirm: 'Continue',
    unavailableNumbers: 'The following numbers are unavailable as they are assigned to other trays',
  },
  CreateTraysDialog: {
    title: (setName: string) => `How many trays are in '${setName}'?`,
    inputLabel: 'No. of Trays',
    maxTrays: (maxTrays: number) => `A maximum of ${maxTrays} trays is allowed`,
    confirm: 'Continue',
    minTrays: 'Please enter a number greater than 0',
  },
  SelectCheckInTrayDialog: {
    trayName: (index: number) => `Tray ${index + 1}`,
    title: 'Please select the tray that you would like to check in',
    confirm: 'Continue',
    back: 'Back',
  },
  SelectCheckOutTrayDialog: {
    title: 'Please select the tray that you would like to check out',
    confirm: 'Continue',
    back: 'Back',
  },
  TrayNameDialog: {
    title: 'What is the name of this tray?',
    inputLabel: 'Enter Tray Name',
    inputDefaultValue: (trayNumber: number) => `Tray ${trayNumber}`,
    confirm: 'Continue',
  },
  ConfirmDecantTrayDialog: {
    title: 'Do you want to decant this tray?',
    cancel: 'No',
    confirm: 'Yes',
  },
  CheckInTrayDecantDetailsPage: {
    // TODO: remove this logic, it can confuse the translation services
    title: ({ setName, trayName, referenceNumber }: { setName: string; trayName: string; referenceNumber: string }) =>
      `Check In {{Set}}: ${setName} - ${trayName}${referenceNumber ? `: ${referenceNumber}` : ''}`,
    retakePhoto: 'Retake Photo',
    continue: 'Continue',
    heading: 'Decanted Image',
    duplicateTrayNumberError: 'Tray number already exists within this surgery',
  },
  ConfirmAnotherDecantedTrayDialog: {
    title: 'Is there another decanted tray?',
    cancel: 'No',
    confirm: 'Yes',
  },
  DeliveryCheckedInSetsDialog: {
    title: 'Delivery Checked In',
    heading: 'This delivery has been processed and the status has been updated to Checked In.',
    viewDeliveryInfoPage: 'View Delivery Information Page',
    viewSurgeryInfoPage: 'View Surgery Information Page',
    goBackToDeliveryList: 'Go Back to Delivery List',
    printAllDeliveryInformation: 'Print All Delivery Information',
    printTrayLabel: 'Print Tray Label',
  },
  DeliveryCheckedInImplantsDialog: {
    title: 'Delivery Checked In',
    heading: 'This delivery has been processed and the status has been updated to Checked In.',
    viewImplantsList: 'View Sterile Implant List',
    viewDeliveryInfoPage: 'Delivery Information Page',
    viewSurgeryInfoPage: 'View Surgery Information Page',
    printAll: 'Print All Delivery Information',
    printImplantList: 'Print Sterile Implant List',
  },
  SurgeryCheckedInImplantsDialog: {
    title: 'Surgery Checked In',
    heading: 'This surgery has been processed and the status has been updated to Checked In.',
    viewSurgeryInfoPage: 'View Surgery Information Page',
  },
  DeliveryCheckedOutDialog: {
    headings: {
      DELIVERY: 'Equipment has been successfully checked out.',
      SETS: 'Your Instrument {{Sets}} have been successfully checked out.',
      IMPLANTS: 'Your Sterile Implants have been successfully checked out.',
    },
    viewDeliveryInfoPage: 'View Delivery Information Page',
    viewSurgeryInfoPage: 'Continue',
    goBackToDeliveryList: 'Go to Delivery List',
  },
  LastTrayInspectionPage: {
    title: 'Last Supplier Tray Inspection',
    pictureList: 'Picture List',
    noImage: 'No Image Provided',
  },
  TrayPictureList: {
    title: 'Tray Picture List',
  },
  CheckInDeliveryDialog: {
    title: 'Do you want to check in this delivery now?',
    cancel: 'No',
    confirm: 'Yes',
  },
  ImplantsCheckedInDialog: {
    titleImplantsCheckedIn: 'All sterile implants have been checked in, what would you like to do next?',
    titleSetsToBeCheckedIn: 'There are {{Sets}} still to be checked in, do you want to check them in now?',
    beginSetCheckIn: 'Begin {{Set}} Check In',
    viewImplantsList: 'View Sterile Implant List',
    viewDeliveryInfoPage: 'Delivery Information Page',
    printImplantList: 'Print Sterile Implant List',
  },
  ImplantsCheckedOutDialog: {
    titleImplantsCheckedOut: 'All sterile implants have been checked out, what would you like to do next?',
    titleSetsToBeCheckedOut: 'There are {{Sets}} still to be checked out, do you want to check them out now?',
    beginSetCheckOut: 'Begin {{Set}} Check Out',
    goToDeliveryInformationPage: 'Return to Delivery Information Page',
    viewSurgeryInfoPage: 'View Surgery Information Page',
    printAllDeliveryInformation: 'Print All Delivery Information',
  },
  SurgeryImplantsCheckedOutDialog: {
    title: 'All sterile implants have been checked out, what would you like to do next?',
    viewSurgeryInfoPage: 'View Surgery Information Page',
    viewSurgeryList: 'View the Surgery List',
  },
  NonScannableImplantsDialog: {
    title: 'Were there any sterile implants sent from the supplier without a scannable barcode?',
    yes: 'Yes, Capture Image',
    continue: 'No, Continue',
  },
  BarcodeScanner: {
    scanBarcode: 'Scan/Input Barcode',
    barcode: 'Barcode',
    scanOrEnterBarcode: 'Scan or enter barcode',
    skipScanning: 'Skip Scanning',
  },
  SetsCheckedInDialog: {
    title: 'All {{Sets}} have been checked in, what would you like to do next?',
    beginImplantCheckIn: 'Begin Implant Check In',
    goToDeliveryInformationPage: 'Return to Delivery Information Page',
    returnToActionItems: 'Return to Action Items',
    printAllDeliveryInformation: 'Print All Delivery Information',
  },
  CheckInDeliveryImplantsOnlyDialog: {
    title: 'Do you have more sterile implants to check in?',
    cancel: 'No',
    confirm: 'Yes',
  },
  DeleteImplantModal: {
    title: 'Delete one implant "{label}" from {deliveryOrSurgery} "{deliveryNumberOrSurgeryId}"?',
    kitTitle:
      'Delete implant kit "{kitName}" with code "{kitCode}" from {deliveryOrSurgery} "{deliveryNumberOrSurgeryId}"?',
    delivery: 'delivery',
    surgery: 'surgery',
    cancel: 'No',
    confirm: 'Yes',
  },
  CheckOutDeliveryImplantsOnlyDialog: {
    title: 'Do you have more sterile implants to check out?',
    cancel: 'No',
    confirm: 'Yes',
  },
  ZeroScannedImplantsDialog: {
    title:
      'Please confirm you are not returning any sterile implants to the manufacturer and you want to move this to check out status?',
    cancel: 'No',
    confirm: 'Yes',
  },
  ZeroImplantsConfirmationDialog: {
    title: 'Are you confirming you’re sending back no sterile implants to the supplier?',
    cancel: 'No',
    confirm: 'Yes',
  },
  SelectContentToCheckInDialog: {
    title: 'Please select what you would like to check in',
    confirm: 'Continue',
  },
  SelectContentToCheckoutDialog: {
    title: 'Please select what you would like to check out',
    confirm: 'Continue',
  },
  ImplantsOnlyConfirmDialog: {
    title: 'There are only sterile implants in this delivery. Do you want to check in the sterile implants only?',
    confirm: 'Continue',
    cancel: 'No, see delivery',
  },
  DecantedImageGallery: {
    title: 'Decanted Trays',
    decantTrayNumberPlaceholder: 'Tray Number',
  },
  SurgeryAttachmentsGallery: {
    title: 'Attachments',
    upload: 'Upload attachment',
    delete: 'Delete attachment',
    empty: 'No attachments uploaded yet',
    uploadFailed: 'Failed to upload attachment',
    deleteFailed: 'Failed to delete attachment',
    openFailed: 'Failed to open attachment',
  },
  UploadSurgeryAttachmentModal: {
    title: 'Upload Attachment',
    upload: 'Upload',
    dropzoneText: 'Drag and drop a file here',
    browse: 'Browse',
    nameLabel: 'Name',
    namePlaceholder: 'Optional friendly name',
    invalidFileType: 'File type not supported',
    uploadFailed: 'Failed to upload attachment',
  },
  InspectionExitWarningDialog: {
    title: 'Exit the set inspection process?',
    body: 'If you proceed, you will lose all unsaved information. Do you want to proceed?',
    cancel: 'No',
    confirm: 'Yes',
  },
  CheckOutInspectDialog: {
    title: 'Are you doing an inspection on the {{Sets}}?',
    cancel: 'No',
    confirm: 'Yes',
  },
  CheckOutSkipInspectionDialog: {
    title: 'Are you going to skip the inspection/s for the whole delivery or specific {{Sets}}?',
    wholeDelivery: 'Whole Delivery',
    specificSets: 'Specific {{Sets}}',
    continue: 'Continue',
  },
  CheckOutSkipInspectionSelectSetsDialog: {
    title: 'Please select the set(s) that you would like to skip the inspection',
    confirm: 'Continue',
  },
  CheckOutSkipInspectionReasonDialog: {
    title: 'Instrument {{Sets}} Skip Inspection',
    subtitle: 'Select the reason for skipping the inspection:',
    checkedOutByLabel: 'Device Manufacturer Rep Name',
    checkedOutByError: 'Please enter the Device Manufacturer Rep Name',
    addNew: 'Add New',
    checkOutDate: 'Check Out Date',
    checkOutDateError: 'Please enter the Check Out Date',
    confirm: 'Continue',
    cancel: 'Exit',
  },
  ConfirmInspectSetDialog: {
    title: 'Are you going to record an inspection on this set?',
    body: 'Set: {setName}',
  },
  TrayNumbersDialog: {
    title: 'Enter Tray Reference Number',
    titlePlural: 'Enter Tray Reference Numbers',
    setName: 'Set: {{setName}}',
    trayReferenceLabel: '{{ordinal}} Tray - Reference Number',
    assetNumberLabel: 'Asset Number (Barcode)',
    assetNumberPlaceholder: 'Click to scan',
    scanBarcodeCaption: 'Scan Barcode',
    previouslyEntered: 'PREVIOUSLY ENTERED TRAY REFERENCE NUMBERS FOR THIS SURGERY',
    confirm: 'Continue',
  },
  QuickCheckOutReasons: {
    [DeliveryQuickCheckOutReason.LimitedTime]: 'Not Required / Limited Time',
    [DeliveryQuickCheckOutReason.WithoutSaviCapture]: 'Already returned without Savi Capture',
    [DeliveryQuickCheckOutReason.SalesRep]: 'Already taken by Device Manufacturer Rep',
    [DeliveryQuickCheckOutReason.CancelledSurgery]: 'Cancelled Surgery',
    [DeliveryQuickCheckOutReason.NoReturnRequired]: 'PSI Guides: No return to Supplier Required',
  },
  CheckOutTrayUsedDialog: {
    title: 'Was the tray used in surgery?',
    cancel: 'No',
    confirm: 'Yes',
  },
  CheckOutTrayNotUsedConfirmDialog: {
    trayNotUsedTitle: 'Confirming this tray was not used in Surgery',
    trayNotUsedBody:
      'This will mark all inspections as passed and use the check in photo for the check out of this tray.',
    continue: 'Continue',
    goBack: 'Go Back',
  },
  CheckOutDeliveryShippingDetailsDialog: {
    title: 'How many shipping items are you sending back to the supplier?',
    numRoadCases: 'No. of Single Roadcases',
    numTubs: 'No. of Tubs',
    numBoxes: 'No. of Boxes',
    numPelicanCases: 'No. of Pelican Cases',
    numDoubleRoadCases: 'No. of Double Roadcases',
    confirm: 'Continue',
    success: 'Delivery shipping details saved successfully',
  },
  SetDetailsPage: {
    setInspectionCompleted: 'Set Inspection Completed',
    setDetails: 'Instrument {{Set}} Details',
    addTray: 'Add Tray',
    completeSetInspection: 'Complete Set Inspection',
    printAllTrayImages: 'Print All Tray Images',
    checkInInspectionDetails: 'Check In Inspection Details',
    checkOutInspectionDetails: 'Check Out Inspection Details',
    editTrayInspection: 'Edit Tray',
    saveTrayInspection: 'Save Tray',
    invalidSetId: 'Invalid set id',
    deleteSet: 'Delete Set',
    deleteSetConfirmation: 'Are you sure you want to delete this set: "{setName}"?',
    yesDeleteSet: 'Yes, Delete Set',
    no: 'No',
    deleteSetSuccess: 'Set deleted successfully.',
  },
  OptionDialog: {
    yes: 'Yes',
    no: 'No',
  },
  DeclareCheckOutReturnMethodDialog: {
    title: 'How is this equipment being returned?',
    returnMethods: {
      logisticsCompany: 'Return via logistics company',
      dmRep: 'Taken by Device Manufacturer Representative',
    },
    checkedOutByLabel: 'Device Manufacturer Rep Name',
    checkedOutByError: 'Please enter the Device Manufacturer Rep Name',
    addNew: 'Add New',
    continue: 'Continue',
  },
  CheckOutNotifySupplierConfirmDialog: {
    title: 'Would you like to notify the supplier that equipment is ready for collection?',
  },
  CheckoutSurgerySuccessDialog: {
    title: 'Your equipment has been checked out.',
    titleNotified: 'Your equipment has been checked out and notification has been sent to the supplier.',
    titleReturned: 'Your equipment has been returned.',
    viewSurgeryList: 'View the Surgery List',
    viewSurgery: 'View Surgery Information',
    generateCheckoutDocument: 'Generate Checkout Document',
  },
  CheckoutDeliverySuccessDialog: {
    title: 'Equipment has been successfully checked out.',
    titleReturned: 'Equipment has been successfully returned.',
    viewDeliveryInformationPage: 'View Delivery Information Page',
    goToDeliveryList: 'Go to Delivery List',
    printAllDeliveryInformation: 'Print All Delivery Information',
    generateCheckoutDocument: 'Generate Checkout Document',
    continue: 'Continue',
  },
  TurnaroundSuccessDialog: {
    title:
      'The equipment has been successfully added to Surgery ID: [Surgery ID], scheduled for [Surgery Date], performed by [Surgeon].',
  },
  TurnaroundDestinationSuccessDialog: {
    sterileImplants: 'Sterile Implants',
    viewDestinationDelivery: 'View Surgery Information Page',
    exit: 'Exit',
  },
  TurnaroundDestinationEntitiesAlreadyPresentDialog: {
    title: ({
      entityNames,
      surgeryId,
      surgeon,
      surgeryDate,
    }: {
      entityNames: string[];
      surgeryId: string;
      surgeon: string;
      surgeryDate: string;
    }) =>
      `${entityNames.join(', ')} ${
        entityNames.length > 1 ? 'have' : 'has'
      } already been marked as turnaround in this destination surgery ${surgeryId} for ${surgeon} on ${surgeryDate}`,
    sterileImplants: 'Sterile Implants',
    cancel: 'Go Back and Select Another Destination Surgery',
  },
  TurnaroundOriginEntitiesAlreadyTurnedAroundDialog: {
    title: ({ entityNames }: { entityNames: string[] }) =>
      `${entityNames.join(', ')} ${
        entityNames.length > 1 ? 'have' : 'has'
      } already been turned around to a different surgery. Do you wish to continue?`,
    sterileImplants: 'Sterile Implants',
    confirm: 'Yes, Continue',
    cancel: 'No, Go Back',
  },
  NotifySupplierSuccessDialog: {
    title: 'We have notified the supplier that this set is ready for immediate collection',
    continue: 'Continue',
  },
  PatientInitialsValidationError: {
    error: 'Invalid Input: Use either 3 letters or 10 digits',
  },
  SelectSetsOrImplantsDialog: {
    title: 'Please select what you would like to check out',
    sterileImplants: 'Sterile Implants',
    dmInstrumentSets: 'Instrument {{Sets}}',
  },
  CheckOutWhatNextDialog: {
    title: 'What would you like to do next?',
    sets: 'Check Out Sets',
    implants: 'Check Out Implants',
    done: "I'm Done",
  },
  SkipDeliveryCheckOut: {
    label: 'Skip Delivery Inspection',
    linkLabel: 'Skip Delivery Check Out (Not recommended)',
  },
  SelectSurgerySetDialog: {
    title: 'Please select the set that you would like to check out',
    goBack: 'Back',
  },
  SavingPleaseWaitDialog: {
    title: {
      saving: 'Saving',
      loading: 'Loading',
    },
    pleaseWait: 'Please wait...',
  },
  AddSetButton: {
    addSet: 'Add an Instrument {{Set}}',
    newSetName: (n: number) => `Instrument {{Set}} ${n}`,
  },
  EditDeliveryExitWarningDialog: {
    title:
      'Do you want to save the changes you made to this delivery? If you exit without saving, your changes will be lost.',
    cancel: 'No',
    confirm: 'Yes',
  },
  TurnaroundPage: {},
  TurnaroundSearchSurgeries: {
    title: {
      origin: 'Search for Surgery Origin',
      destination: 'Search for Surgery Destination',
    },
    search: 'Search',
    formLabels: {
      supplier: 'Supplier',
      orderNumber: 'Order Number',
      surgeryReference: 'Surgery ID',
      deliveryNumber: 'Delivery Number',
      surgeon: 'Surgeon',
      surgeryDate: 'Surgery Date',
    },
    tableHeaders: {
      surgeryReference: 'Surgery ID',
      supplier: 'Supplier',
      surgeon: 'Surgeon',
      surgeryDate: 'Surgery Date',
      deliveryNumber: 'Delivery No',
      patientInitials: 'Patient Initials',
    },
    noSurgeries: 'No surgeries found. Try different search criteria.',
    pleaseSearch: 'Please search for surgeries above.',
  },
  TurnaroundSelectEntities: {
    title: {
      origin: 'Available Instrument {{Sets}} / Sterile Implants for Turnaround',
      destination: 'Instrument {{Sets}} / Sterile Implants to be Turned Around',
    },
    noSets: 'No {{Sets}} found. Try selecting a different delivery.',
  },
  TurnaroundMoveEntities: {
    selectAll: 'Select All »',
    select: 'Select ›',
    deselect: '‹ Deselect',
    deselectAll: '« Deselect All',
  },
  TurnaroundExistingSurgeryButton: {
    existingSurgery: 'Turnaround to the Selected Surgery',
  },
  TurnaroundNewSurgeryButton: {
    newSurgery: 'Turnaround to a New Surgery',
  },
  TurnaroundListButton: {
    viewTurnaroundList: 'View Turnaround List',
  },
  TurnaroundImplants: {
    sterileImplants: 'Sterile Implants',
  },
  TurnaroundList: {
    columns: {
      status: 'Status',
      surgeryOrigin: 'Surgery Origin',
      itemsTurnedAround: 'Items Turned Around',
      surgeryDestination: 'Surgery Destination',
      actions: 'Actions',
    },
    noResults: 'No results found',
    surgeryDetails: {
      supplier: 'Supplier',
      surgeon: 'Surgeon',
      surgeryDate: 'Surgery Date',
      surgeryId: 'Surgery ID',
      orderNumber: 'Order No.',
      deliveryNumber: 'Delivery No.',
    },
    view: 'View',
    cancel: 'Cancel',
    cancelConfirmationTitle: 'Are you sure you want to cancel this turnaround?',
    cancelSuccess: 'Turnaround cancelled successfully',
  },
  LoggedOutPage: {
    welcome: 'Welcome',
    loggedOut: 'Logged Out',
    reason: {
      login: 'Please login to continue',
      user: 'You have been logged out',
      inactivity: 'You have been logged out due to inactivity',
      switch: 'Select a user to continue',
      error: 'An error occurred',
      password: 'Your password has expired and needs to be reset before you can log in',
    },
    subscriptionInfo: 'If you do not have a login account please contact your administrator',
    recoverInfo: 'If you forgot your password you can recover it via the {loginPageLink}',
    loginPage: 'Login Page',
    login: 'Login',
    switchAccount: 'Account',
    differentAccount: 'Use a different account',
    pinHint: 'Log in once with your password on this device to enable PIN login',
    pinExpired: 'Your PIN login has expired. Log in with your password, then set your PIN again.',
    unknownError: 'An unknown error occurred',
    invalidOrgLink: 'This login link is missing an organisation. Please contact your administrator for a new link.',
    passwordReset: {
      instructionsTitle: 'Please follow the following steps:',
      instructions: [
        'Click the Reset Password button below',
        'If prompted, enter your Organisation and click "Continue"',
        'On the Login page, click the "Forgot Password?" button',
        'On the Forgot Password page, enter your email address and click the "Continue" button',
        'Follow the instructions in the email you receive',
        'Return to the app and login as usual, with your new password',
      ],
      resetPassword: 'Reset Password',
    },
  },
  TrayWithoutInspectionsDialog: {
    title: ({ trayName }: { trayName: string }) =>
      `The '${trayName}' tray has no inspections, what would you like to do?`,
    titleLoading: 'Loading...',
    addInspection: 'Add Inspection',
    deleteTray: 'Delete Tray',
    deletedTray: 'Tray deleted successfully',
  },
  OrdersActiveHospitalsFilter: {
    button: (active: boolean) => `Active Hospitals: ${active ? 'ON' : 'OFF'}`,
  },
  ImplantButtons: {
    implantList: 'Sterile Implant List',
    turnaroundOrigin: 'Turnaround',
    turnaroundDestination: 'Turnaround',
    checkOutMoreImplants: 'Check Out More Sterile Implants',
    checkInImplants: 'Check In Sterile Implants',
    checkOutImplants: 'Check Out Sterile Implants',
    skipCheckInImplants: 'Skip Implant Check In',
    skipCheckOutImplants: 'Skip Implant Check Out',
  },
  TurnaroundStage: {
    PENDING: 'PENDING',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
  },
  SetAlreadyTurnedaroundDialog: {
    title: 'Equipment has been successfully added to the new surgery',
    confirm: 'OK',
  },
  TraySummaryV2: {
    printTrayImage: 'Print Tray Image',
    retakePhoto: 'Retake Photo',
    takePhoto: 'Take a Photo',
    deleteTray: 'Delete Tray',
    emptyTrayReference: 'Invalid Tray Number. Please ensure Tray Number is a numeric value greater than zero.',
    invalidTrayReference:
      'Please enter a different tray number; as the tray number you entered is already being used on this surgery.',
    unavailableTrayReferences: 'The following tray numbers are {not} available: {trayReferences}.',
    close: 'Close',
    not: 'not',
  },
  SaveAndReprintDeliveryInfoDialog: {
    title: 'Please save and reprint the delivery information',
    confirm: 'Save and Reprint',
    cancel: 'Cancel',
  },
  TurnaroundDeliveryChip: {
    turnaround: 'Turnaround',
  },
  SkipImplantCheckInVerifyCountDialog: {
    title: 'Skip Inspection',
    subtitle: 'Do you want to input the count of the individual sterile implants supplied?',
    confirm: 'Yes',
    cancel: 'No',
  },
  SkipImplantCheckOutVerifyCountDialog: {
    title: 'Skip Inspection',
    subtitle:
      'Do you want to input the count of the individual sterile implants supplied before you return the items to the supplier?',
    confirm: 'Yes',
    cancel: 'No',
  },
  SkipImplantCheckInReasons: {
    limitedTime: 'Limited time (i.e. Patient on table)',
    withoutSaviCapture: 'Already sent to theatre without using Savi Capture',
    salesRep: 'Device Manufacturer Rep checked and sent to theatre',
    processedUsingRfidMachine: 'Processed using RFID machine',
  },
  SkipImplantCheckOutReasons: {
    limitedTime: 'Limited time',
    withoutSaviCapture: 'Already Returned without using Savi Capture',
    salesRep: 'Already Checked Out by Device Manufacturer Rep',
    processedUsingRfidMachine: 'Processed using RFID machine',
    // this should only be possible for checkout
    noImplantReturned: 'No Sterile Implants Returned to Supplier and Updated to Checked Out',
  },
  SkipImplantCheckInReasonDialog: {
    title: 'Skip Inspection',
    countLabel: 'Check In Count for Sterile Implants',
    dateLabel: 'Check In Date',
    subtitle: 'Select the reason for skipping the inspection:',
    confirm: 'Continue',
    cancel: 'Exit',
  },
  SkipImplantCheckOutReasonDialog: {
    title: 'Skip Inspection',
    countLabel: 'Check Out Count for Sterile Implants',
    dateLabel: 'Check Out Date',
    subtitle: 'Select the reason for skipping the inspection:',
    confirm: 'Continue',
    cancel: 'Exit',
  },
  PrintSurgeryInfoModal: {
    title: 'Print Surgery Information',
    selectDeliveries: 'Select Deliveries to Print',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    printButton: 'Print',
    pleaseWait: 'Preparing all Information sheets to be printed. Please wait, this may take a while.',
    printSuccess: 'Surgery information printed successfully',
    printFailure: 'Failed to print surgery information',
  },
  MultiSelectModal: {
    title: 'Select Items',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    confirm: 'Continue',
    close: 'Close',
    pleaseWait: 'Please wait...',
    noItemsFound: 'No items found',
  },
  VersionUpdateBanner: {
    newVersionAvailable:
      "It appears that you're using an outdated version (v{currentVersion}) of the app - Refresh now to get the latest (v{latestVersion}) features & improvements.",
    reloadPage: 'Re-load the app',
    dismissForNow: 'Dismiss for now',
  },
  ImplantStatusChip: {
    yes: 'Yes',
    skipped: 'Skipped',
  },
  IndividualItemStatusChip: {
    individualItem: 'Individual Item',
  },
  SkippedInspectionMessage: {
    skipped: 'Skipped',
    title: {
      checkIn: 'Check In Inspection for Implants Skipped',
      checkOut: 'Check out Inspection for Implants Skipped',
    },
    description: ({ user, date, reason }: { user: string; date: string; reason: string }) =>
      `User ${user} on ${date} skipped the inspection for this delivery due to ${reason}.`,
    descriptionNoReason: ({ user, date, reason: _ }: { user: string; date: string; reason: string }) =>
      `User ${user} on ${date} skipped the inspection for this delivery.`,
  },
  FeatureUnavailableNotice: {
    title: 'Feature Unavailable',
    featureIsOff: 'This feature is currently turned off.',
    learnMoreBelow: 'Click the button below to learn more or gain access.',
    learnMore: 'Learn More or Gain Access',
    successMessage: 'Thanks we have let our team know',
  },
  ReturnDeliveryButton: {
    returnDelivery: 'Return Delivery',
    returnSurgery: 'Return Equipment',
  },
  LockChip: {
    locked: 'LOCKED',
    unlocked: 'UNLOCKED',
  },
  ActiveChip: {
    active: 'ACTIVE',
    inactive: 'INACTIVE',
  },
  VerifiedChip: {
    verified: 'VERIFIED',
    unverified: 'UNVERIFIED',
  },
  LockSurgeonDialog: {
    title: 'Lock Surgeon Information',
    confirm: 'Yes, Lock Surgeon',
    lockSuccess: 'Surgeon has been locked successfully.',
    lockError: 'Failed to lock the surgeon.',
    areYouSure: 'Are all the details correct?',
    onceLocked: "Once locked, you cannot unlock nor change the surgeon's:",
    lockedFields: {
      title: 'Title',
      firstName: 'First name',
      lastName: 'Last name',
      ahpra: 'AHPRA Id',
    },
    permanentAction: 'This action is permanent and cannot be undone.',
  },
  // Terms whose wording is configurable per tenant (e.g. Set vs Caddy). Lang strings opt in by
  // including a term's tokenNames as {{Token}} placeholders; useLanguage() resolves them
  // automatically - see locale/withConfigurableTerms.
  ConfigurableTerms: {
    Set: {
      tokenNames: ['Set', 'Sets'] as [string, string],
      options: {
        [SetTerminology.Set]: { singular: 'Set', plural: 'Sets' },
        [SetTerminology.Caddy]: { singular: 'Caddy', plural: 'Caddies' },
      },
    },
  },
};

export default en;
